import { Injectable } from '@angular/core';
import { ModalController, ModalOptions } from '@ionic/angular';
import { TObjectWithKeys } from '@monorepo/types';

type TDismissDetails = { data?: unknown; role?: string };
type ExactDismiss<T> = TDismissDetails & {
  [K in Exclude<keyof T, keyof TDismissDetails>]: never;
};

type TModalOptions<TProps extends object | void = void> = TProps extends void
  ? Omit<ModalOptions, 'componentProps'>
  : Omit<ModalOptions, 'componentProps'> & {
      componentProps: TObjectWithKeys<Extract<TProps, object>>;
    };

type TModalResult<TDismiss extends TDismissDetails = TDismissDetails> = {
  ionModal: HTMLIonModalElement;
  present: () => Promise<void>;
  onDidDismiss: () => Promise<TDismiss>;
  onWillDismiss: () => Promise<TDismiss>;
};

/**
 * Type-safe wrapper around Ionic's `ModalController`.
 *
 * - Defaults to `unknown` instead of Ionic's `any` for dismiss data.
 * - `componentProps` required only when `TProps` is specified, and must be an object.
 * - `TDismiss` constrains dismiss result to `{ data; role }` shape.
 * - If no props needed but dismiss data is typed, pass `void` as `TProps`.
 */
@Injectable({ providedIn: 'root' })
export class TypedModalController {
  constructor(private readonly modalController: ModalController) {}

  /**
   * Wrapper for Ionic ModalController's create method.
   *
   * @example
   * ```ts
   * const modal = await this.typedModalService.create<
   *   { userId: string },
   *   { data: { confirmed: boolean }; role: 'save' | 'cancel' }
   * >({
   *   component: UserModal,
   *   componentProps: { userId: 123 }, // TS Error: Type 'number' is not assignable to type 'string'.
   * });
   * await modal.present();
   * const { data, role } = await modal.onDidDismiss(); // data: { confirmed: boolean }, role: 'save' | 'cancel'
   * ```
   */
  async create<TProps extends object | void = void, TDismiss extends ExactDismiss<TDismiss> = TDismissDetails>(
    opts: TModalOptions<TProps>,
  ): Promise<TModalResult<TDismiss>> {
    const ionModal = await this.modalController.create(opts);
    return {
      ionModal,
      present: () => ionModal.present(),
      onDidDismiss: () => ionModal.onDidDismiss() as Promise<TDismiss>,
      onWillDismiss: () => ionModal.onWillDismiss() as Promise<TDismiss>,
    };
  }

  /**
   * [Shorthand] - Creates and immediately presents modal.
   *
   * @example
   * ```ts
   * // No props, untyped dismiss
   * const modal = await this.typedModalService.createAndPresent({ component: InfoModal });
   *
   * // Typed dismiss, no props - use void
   * const modal = await this.typedModalService.createAndPresent<
   *   void,
   *   { data: { saved: boolean }; role: 'confirm' }
   * >({ component: InfoModal });
   * ```
   */
  async createAndPresent<
    TProps extends object | void = void,
    TDismiss extends ExactDismiss<TDismiss> = TDismissDetails,
  >(opts: TModalOptions<TProps>): Promise<Omit<TModalResult<TDismiss>, 'present'>> {
    const { present, ...rest } = await this.create<TProps, TDismiss>(opts);
    await present();

    return rest;
  }

  /**
   * Wrapper for Ionic ModalController's dismiss method.
   * When `TDismiss` is provided, `data` and `role` are required and strictly typed.
   *
   * @example
   * ```ts
   * // Untyped — all args optional
   * this.typedModalService.dismiss();
   *
   * // Typed — data and role required
   * this.typedModalService.dismiss<{ data: { confirmed: boolean }; role: 'save' | 'cancel' }>(
   *   { confirmed: true }, 'save',
   * );
   * this.typedModalService.dismiss<{ data: { confirmed: boolean }; role: 'save' | 'cancel' }>(
   *   { confirmed: true }, 'wrong', // TS error
   * );
   * ```
   */
  dismiss<TDismiss extends ExactDismiss<TDismiss> | void = void>(
    ...args: TDismiss extends TDismissDetails
      ? [data: TDismiss['data'], role: TDismiss['role'], id?: string]
      : [data?: unknown, role?: string, id?: string]
  ): ReturnType<ModalController['dismiss']> {
    const [data, role, id] = args;
    return this.modalController.dismiss(data, role, id);
  }

  getTop(): ReturnType<ModalController['getTop']> {
    return this.modalController.getTop();
  }
}
